import { useEffect, useRef, useState } from "react";

const API_BASE = ((import.meta as any).env?.BASE_URL || "/").replace(/\/$/, "") + "/api";

const PLATFORM_URL = (import.meta.env.VITE_PLATFORM_URL as string | undefined)?.trim() || "https://nexuscart.co/";

const SHADOW_CSS = `
  :host {
    all: initial !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    contain: layout style;
    width: 100% !important;
    height: auto !important;
    background: rgba(0, 0, 0, 0.4) !important;
    border-top: 1px solid #1f2937 !important;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important;
  }
  .wrap {
    max-width: 1280px;
    margin: 0 auto;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  a {
    font-size: 11px;
    color: #6b7280;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color 0.15s;
  }
  a:hover { color: #d1d5db; }
  .brand { font-weight: 700; color: #d1d5db; }
`;

function buildBadgeHtml(lang: string): string {
  const label = lang === "ar" ? "مدعوم بواسطة" : "Powered by";
  return `
    <style>${SHADOW_CSS}</style>
    <div class="wrap" dir="${lang === "ar" ? "rtl" : "ltr"}">
      <a href="${PLATFORM_URL}" target="_blank" rel="noopener noreferrer">
        <span>${label}</span>
        <span class="brand">Nexus Cart</span>
      </a>
    </div>
  `;
}

/**
 * Platform attribution badge rendered in a CLOSED Shadow DOM.
 *
 * Why Shadow DOM (closed mode):
 *  - External CSS cannot pierce the shadow boundary, so a tenant cannot hide
 *    the badge with structural selectors like `footer > div:last-child{display:none}`
 *    that would bypass the server-side anchor scanner.
 *  - `mode: "closed"` means the host element does NOT expose `.shadowRoot`
 *    to JavaScript running in the same document, so a tenant's customJs
 *    cannot reach inside to mutate or remove the link.
 *  - The host element itself is kept visible by `installBrandingWatchdog()`
 *    in `lib/branding-watchdog.ts`, which uses `!important` overrides and a
 *    MutationObserver to re-assert visibility every ~2 seconds.
 *
 * The host carries the same anchor classes/data-attributes that the server
 * branding-lock scanner refuses to allow in customCss/customJs, so any
 * tenant payload mentioning them is rejected at the API layer too.
 */
export function PlatformBadge({ lang }: { lang: string }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [hide, setHide] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/_meta/platform-badge`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { hide: false }))
      .then((d) => { if (!cancelled) { setHide(!!d?.hide); setReady(true); } })
      .catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || hide) return;
    const host = hostRef.current;
    if (!host) return;

    // Use a sentinel attribute so we don't double-attach across re-renders.
    if ((host as any).__nxShadowAttached) {
      return;
    }
    try {
      const root = host.attachShadow({ mode: "closed" });
      root.innerHTML = buildBadgeHtml(lang);
      (host as any).__nxShadowAttached = true;
      (host as any).__nxShadowRoot = root;
    } catch {
      // attachShadow may fail in very old browsers — fall back to plain DOM
      // so the badge still renders, even though it's no longer hardened.
      host.innerHTML = buildBadgeHtml(lang);
    }
  }, [lang]);

  // Re-render the shadow DOM contents if the language changes.
  useEffect(() => {
    const host = hostRef.current as any;
    if (host?.__nxShadowRoot) {
      host.__nxShadowRoot.innerHTML = buildBadgeHtml(lang);
    }
  }, [lang]);

  if (!ready || hide) return null;

  return (
    <div
      ref={hostRef}
      className="nx-platform-badge"
      data-nx-platform-badge="1"
      data-nexus-branding="powered-by"
    />
  );
}

/**
 * JSON-LD structured data declaring Nexus Cart as the platform provider.
 * Legitimate, transparent SEO signal — Google understands `provider` and
 * `isBasedOn` on Organization schema. Renders one <script type="application/ld+json">
 * tag in the storefront. Independent of the visible footer badge so that
 * even white-label tenants still emit the structured data (visible to crawlers
 * and tools like Schema.org validators, but not to ordinary visitors).
 */
export function PlatformProviderJsonLd({ storeName, storeUrl }: { storeName: string; storeUrl?: string }) {
  const PLATFORM_URL = ((import.meta as any).env?.VITE_PLATFORM_URL as string | undefined)?.trim() || "https://nexuscart.co/";
  const data = {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    "name": storeName,
    ...(storeUrl ? { "url": storeUrl } : {}),
    "provider": {
      "@type": "Organization",
      "name": "Nexus Cart",
      "alternateName": "نكسس كارت",
      "url": PLATFORM_URL,
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
